import { useEffect, useMemo, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Group, Loader, Stack, Text } from '@mantine/core';
import { Cpu } from 'lucide-react';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import SurfaceBreadcrumbRow from '@/shared/surfaces/SurfaceBreadcrumbRow';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import SideRailPageLayout from '@/shared/surfaces/SideRailPageLayout';
import TopicSummaryPanel from '@/shared/surfaces/TopicSummaryPanel';
import EvidenceTable from '@/shared/surfaces/EvidenceTable';
import SurfaceAssistScope, { SurfaceAssistToolbar } from '@/shared/surfaces/assist/SurfaceAssistScope';
import { useAssistFilters } from '@/shared/surfaces/assist/AssistSurfaceProvider';
import { useSurface } from '@/shared/surfaces/useSurface';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import type { StatItem } from '@/design/StatBand';
import type { AnalyticsSurface } from '@/shared/surfaces/AnalyticsSurface';
import type { AnalyticsField, AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import { formatCount, formatPercent, formatTokens, formatUsd, humanUser } from '@/shared/analytics/MetricFormatting';
import { BehaviorModelFitPromptsViewModel } from './BehaviorModelFitPromptsViewModel';
import { MODEL_FIT_PROMPT_COLUMNS } from './ModelFitPromptColumns';
import { recordWaste } from './ModelFitRecord';
import ModelFitScorePanel from './ModelFitScorePanel';

const PAGE_SIZE = 25;
type FitPromptScope = 'user' | 'model';

/** Behavior › Model Fit drill: every prompt behind a person or a model, with its fit score and per-prompt waste. */
export default function BehaviorModelFitPromptsPage({ scope }: { scope: FitPromptScope }): ReactElement {
    const surface = useSurface('behavior');
    const { value } = useParams<{ value: string }>();
    if (surface === undefined || value === undefined || value.length === 0) return <TopicNotFound />;
    return (
        <SurfaceAssistScope surface={surface}>
            <Body key={`${scope}:${value}`} surface={surface} scope={scope} value={value} />
        </SurfaceAssistScope>
    );
}

function Body({ surface, scope, value }: { surface: AnalyticsSurface; scope: FitPromptScope; value: string }): ReactElement {
    const vm = useViewModel(BehaviorModelFitPromptsViewModel);
    const ambientFilters = useAssistFilters();
    const field: AnalyticsField = scope === 'user' ? 'user_id' : 'model';
    const filters = useMemo<AnalyticsFilter[]>(() => [{ field, values: [value] }, ...ambientFilters], [field, value, ambientFilters]);

    useEffect(() => {
        void vm.load(filters);
    }, [vm, filters]);

    const title = scope === 'user' ? humanUser(value) : value;
    const basePath = surface.basePath;

    const wasteCost = vm.records.reduce((sum, record) => sum + recordWaste(record).cost, 0);
    const wasteTokens = vm.records.reduce((sum, record) => sum + recordWaste(record).tokens, 0);
    const misfitCount = vm.records.reduce((n, record) => n + (recordWaste(record).misfit ? 1 : 0), 0);

    const stats: StatItem[] = [
        { label: 'Prompts', value: formatCount(vm.summary.turns) },
        { label: 'Misfit', value: vm.records.length > 0 ? formatPercent(misfitCount / vm.records.length) : '—', hint: 'tier-mismatched' },
        { label: 'Waste cost', value: formatUsd(wasteCost) },
        { label: 'Waste tokens', value: formatTokens(wasteTokens) },
    ];

    const rail = (
        <Stack gap="md">
            <TopicSummaryPanel stats={stats} />
            <ModelFitScorePanel records={vm.records} />
        </Stack>
    );

    return (
        <Box pb="xl">
            <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

            <SurfaceBreadcrumbRow items={[{ label: 'Model Fit', to: `${basePath}/model-fit` }, { label: title }]} />

            <PageHeaderComponent
                icon={Cpu}
                eyebrow={scope === 'user' ? 'Model Fit · person' : 'Model Fit · model'}
                title={title}
                subtitle={
                    scope === 'user' ?
                        'Every prompt this person ran — the model it went to, its fit score, and the waste when the tier didn’t suit the task.'
                    :   'Every prompt sent to this model — who ran it, its fit score, and the waste when the tier didn’t suit the task.'
                }
            />

            {vm.errorMessage !== null && (
                <Text size="sm" c="red" mb="md">
                    {vm.errorMessage}
                </Text>
            )}

            {vm.loading && vm.records.length === 0 ?
                <Group justify="center" gap="sm" py="xl">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">
                        Loading prompts…
                    </Text>
                </Group>
            :   <SideRailPageLayout rail={rail}>
                    <EvidenceTable records={vm.records} accent="indigo" columns={MODEL_FIT_PROMPT_COLUMNS} clientPageSize={PAGE_SIZE} persistenceKey={`model-fit-prompts:${scope}`} />
                </SideRailPageLayout>
            }
        </Box>
    );
}
